// ============================================================================
// SECOND SCREEN CONTEXT - BroadcastChannel Subscriber
// Receives state from the main window and provides it to widgets via context
// ============================================================================

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type {
  SecondScreenState,
  SecondScreenMessage,
  SecondScreenRequestSnapshot,
  ScreenTheme,
  CinematicCard,
  CinematicContribution,
  OverscanSettings,
} from '../types/second-screen.types';
import type { AvatarSuggestion } from '../services/workshop';

export interface ActiveMindBubble {
  id: string;
  avatarRole: string;
  avatarName: string;
  thought: string;
}

const THEME_KEY = 'second-screen-theme';
const OVERSCAN_KEY_PREFIX = 'second-screen-overscan-';
const DEFAULT_OVERSCAN: OverscanSettings = { top: 0, bottom: 0, left: 0, right: 0 };
const MAX_MIND_BUBBLES = 12;
const MAX_CINEMATIC_CARDS = 5;
const MAX_CINEMATIC_CONTRIBUTIONS = 4;

interface SecondScreenContextValue {
  state: SecondScreenState | null;
  isConnected: boolean;
  lastUpdated: number | null;
  activeSpotlight: AvatarSuggestion | null;
  clearSpotlight: () => void;
  activeMindBubbles: ActiveMindBubble[];
  clearMindBubble: (id: string) => void;
  activeCinematicCards: CinematicCard[];
  clearCinematicCard: (id: string) => void;
  activeCinematicContributions: CinematicContribution[];
  clearCinematicContribution: (id: string) => void;
  theme: ScreenTheme;
  toggleTheme: () => void;
  overscan: OverscanSettings;
}

const SecondScreenContext = createContext<SecondScreenContextValue>({
  state: null,
  isConnected: false,
  lastUpdated: null,
  activeSpotlight: null,
  clearSpotlight: () => {},
  activeMindBubbles: [],
  clearMindBubble: () => {},
  activeCinematicCards: [],
  clearCinematicCard: () => {},
  activeCinematicContributions: [],
  clearCinematicContribution: () => {},
  theme: 'dark',
  toggleTheme: () => {},
  overscan: DEFAULT_OVERSCAN,
});

const CONNECTION_TIMEOUT_MS = 45_000;

export function SecondScreenDataProvider({
  sessionId,
  screenNumber,
  children,
}: {
  sessionId: string;
  screenNumber?: number;
  children: React.ReactNode;
}) {
  const [state, setState] = useState<SecondScreenState | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [activeSpotlight, setActiveSpotlight] = useState<AvatarSuggestion | null>(null);
  const [activeMindBubbles, setActiveMindBubbles] = useState<ActiveMindBubble[]>([]);
  const [activeCinematicCards, setActiveCinematicCards] = useState<CinematicCard[]>([]);
  const [activeCinematicContributions, setActiveCinematicContributions] = useState<CinematicContribution[]>([]);
  const [theme, setTheme] = useState<ScreenTheme>(() => {
    try {
      const stored = localStorage.getItem(THEME_KEY);
      return stored === 'light' ? 'light' : 'dark';
    } catch { return 'dark'; }
  });
  const [overscan, setOverscan] = useState<OverscanSettings>(() => {
    try {
      const raw = localStorage.getItem(`${OVERSCAN_KEY_PREFIX}${sessionId}-screen${screenNumber ?? 1}`);
      return raw ? JSON.parse(raw) : DEFAULT_OVERSCAN;
    } catch { return DEFAULT_OVERSCAN; }
  });
  const channelRef = useRef<BroadcastChannel | null>(null);
  const connectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spotlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mindBubbleTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const cinematicCardTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const cinematicContributionTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const resetConnectionTimeout = useCallback(() => {
    if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
    connectionTimeoutRef.current = setTimeout(() => {
      setIsConnected(false);
    }, CONNECTION_TIMEOUT_MS);
  }, []);

  const clearSpotlight = useCallback(() => {
    setActiveSpotlight(null);
    if (spotlightTimerRef.current) clearTimeout(spotlightTimerRef.current);
  }, []);

  const clearMindBubble = useCallback((id: string) => {
    setActiveMindBubbles(prev => prev.filter(b => b.id !== id));
    const timer = mindBubbleTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      mindBubbleTimersRef.current.delete(id);
    }
  }, []);

  const clearCinematicCard = useCallback((id: string) => {
    setActiveCinematicCards(prev => prev.filter(c => c.id !== id));
    const timer = cinematicCardTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      cinematicCardTimersRef.current.delete(id);
    }
  }, []);

  const clearCinematicContribution = useCallback((id: string) => {
    setActiveCinematicContributions(prev => prev.filter(c => c.id !== id));
    const timer = cinematicContributionTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      cinematicContributionTimersRef.current.delete(id);
    }
  }, []);

  useEffect(() => {
    const channel = new BroadcastChannel(`spectrum-second-screen-${sessionId}`);
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent<SecondScreenMessage>) => {
      const msg = event.data;
      const now = Date.now();

      // If the message targets a specific screen and we're not that screen, ignore it
      if (msg.targetScreen != null && screenNumber != null && msg.targetScreen !== screenNumber) {
        return;
      }

      switch (msg.type) {
        case 'snapshot':
          setState(prev => {
            const payload = msg.payload;
            if (!prev) return payload;
            return {
              ...payload,
              inspirationCards: payload.inspirationCards?.length
                ? payload.inspirationCards
                : prev.inspirationCards,
              backgroundInfo: {
                customerName: payload.backgroundInfo?.customerName || prev.backgroundInfo?.customerName || '',
                industry:     payload.backgroundInfo?.industry     || prev.backgroundInfo?.industry     || '',
                context:      payload.backgroundInfo?.context      || prev.backgroundInfo?.context      || '',
              },
            };
          });
          setIsConnected(true);
          setLastUpdated(now);
          resetConnectionTimeout();
          break;

        case 'patch':
          setState(prev => prev ? { ...prev, ...msg.fields } : null);
          setIsConnected(true);
          setLastUpdated(now);
          resetConnectionTimeout();
          break;

        case 'ping':
          setIsConnected(true);
          resetConnectionTimeout();
          // Main page just started — request fresh state so refresh doesn't leave screen blank
          channel.postMessage({
            type: 'request_snapshot',
            sessionId,
            seq: 0,
            ts: Date.now(),
          } as SecondScreenRequestSnapshot);
          break;

        case 'spotlight':
          setActiveSpotlight(msg.contribution);
          if (msg.duration) {
            if (spotlightTimerRef.current) clearTimeout(spotlightTimerRef.current);
            spotlightTimerRef.current = setTimeout(() => {
              setActiveSpotlight(null);
            }, msg.duration);
          }
          break;

        case 'clear_spotlight':
          clearSpotlight();
          break;

        case 'pin_content':
          setState(prev => {
            if (!prev) return prev;
            const existing = prev.pinnedContent || [];
            // Avoid duplicates
            if (existing.some(p => p.id === msg.item.id)) return prev;
            return { ...prev, pinnedContent: [...existing, msg.item] };
          });
          setIsConnected(true);
          setLastUpdated(Date.now());
          resetConnectionTimeout();
          break;

        case 'unpin_content':
          setState(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              pinnedContent: (prev.pinnedContent || []).filter(p => p.id !== msg.itemId),
            };
          });
          setIsConnected(true);
          setLastUpdated(Date.now());
          resetConnectionTimeout();
          break;

        case 'mind_bubble': {
          const id = `${msg.avatarRole}-${Date.now()}`;
          const bubble: ActiveMindBubble = { id, avatarRole: msg.avatarRole, avatarName: msg.avatarName, thought: msg.thought };
          setActiveMindBubbles(prev => {
            const next = [...prev, bubble];
            // Drop oldest if cap exceeded
            return next.length > MAX_MIND_BUBBLES ? next.slice(next.length - MAX_MIND_BUBBLES) : next;
          });
          // 90s safety-net: SingleBubble handles normal dismissal via onDismiss,
          // but if the component fails to do so (e.g. bubble was still queued/waiting),
          // this ensures it's eventually cleared. Must exceed SingleBubble's own 60s linger.
          const mbTimer = setTimeout(() => {
            setActiveMindBubbles(prev => prev.filter(b => b.id !== id));
            mindBubbleTimersRef.current.delete(id);
          }, 90_000);
          mindBubbleTimersRef.current.set(id, mbTimer);
          break;
        }

        case 'task_update':
          setState(prev => prev ? { ...prev, activeTasks: msg.tasks } : null);
          break;

        case 'requirements_focus':
          setState(prev => prev ? {
            ...prev,
            requirementsFocus: { activePersonaId: msg.activePersonaId, storiesScrollTop: msg.storiesScrollTop },
          } : null);
          break;

        case 'overscan':
          setOverscan(msg.settings);
          localStorage.setItem(
            `${OVERSCAN_KEY_PREFIX}${sessionId}-screen${screenNumber ?? 1}`,
            JSON.stringify(msg.settings)
          );
          break;

        case 'cinematic_contribution': {
          const { contribution } = msg;
          setActiveCinematicContributions(prev => {
            if (prev.some(c => c.id === contribution.id)) return prev;
            const next = [...prev, contribution];
            return next.length > MAX_CINEMATIC_CONTRIBUTIONS ? next.slice(next.length - MAX_CINEMATIC_CONTRIBUTIONS) : next;
          });
          const ccContribTimer = setTimeout(() => {
            setActiveCinematicContributions(prev => prev.filter(c => c.id !== contribution.id));
            cinematicContributionTimersRef.current.delete(contribution.id);
          }, 90_000);
          cinematicContributionTimersRef.current.set(contribution.id, ccContribTimer);
          break;
        }

        case 'cinematic_card': {
          const card = msg.card;
          setActiveCinematicCards(prev => {
            // Avoid duplicates
            if (prev.some(c => c.id === card.id)) return prev;
            const next = [...prev, card];
            // Cap at max; drop oldest
            return next.length > MAX_CINEMATIC_CARDS ? next.slice(next.length - MAX_CINEMATIC_CARDS) : next;
          });
          // 90s safety-net timer (card component handles its own 45s linger + fade)
          const ccTimer = setTimeout(() => {
            setActiveCinematicCards(prev => prev.filter(c => c.id !== card.id));
            cinematicCardTimersRef.current.delete(card.id);
          }, 90_000);
          cinematicCardTimersRef.current.set(card.id, ccTimer);
          break;
        }
      }
    };

    // Request initial snapshot
    const request: SecondScreenRequestSnapshot = {
      type: 'request_snapshot',
      sessionId,
      seq: 0,
      ts: Date.now(),
    };
    channel.postMessage(request);

    // Start connection timeout
    resetConnectionTimeout();

    return () => {
      channel.close();
      channelRef.current = null;
      if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
      if (spotlightTimerRef.current) clearTimeout(spotlightTimerRef.current);
      mindBubbleTimersRef.current.forEach(t => clearTimeout(t));
      mindBubbleTimersRef.current.clear();
      cinematicCardTimersRef.current.forEach(t => clearTimeout(t));
      cinematicCardTimersRef.current.clear();
      cinematicContributionTimersRef.current.forEach(t => clearTimeout(t));
      cinematicContributionTimersRef.current.clear();
    };
  }, [sessionId, screenNumber, resetConnectionTimeout, clearSpotlight]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const next: ScreenTheme = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem(THEME_KEY, next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    state, isConnected, lastUpdated, activeSpotlight, clearSpotlight,
    activeMindBubbles, clearMindBubble,
    activeCinematicCards, clearCinematicCard,
    activeCinematicContributions, clearCinematicContribution,
    theme, toggleTheme,
    overscan,
  }), [state, isConnected, lastUpdated, activeSpotlight, clearSpotlight,
      activeMindBubbles, clearMindBubble,
      activeCinematicCards, clearCinematicCard,
      activeCinematicContributions, clearCinematicContribution,
      theme, toggleTheme,
      overscan]);

  return (
    <SecondScreenContext.Provider value={value}>
      {children}
    </SecondScreenContext.Provider>
  );
}

export function useSecondScreenData() {
  return useContext(SecondScreenContext);
}
